import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { userStorageKey } from "@medoma/shared-employee/userStorageKey";
import { useUserProfile } from "@/api/Users";

type InitialLeadingPanelSize = `${number}%` | `${number}px` | number;
type PanelSize = number;

const KEYBOARD_STEP = 16;

// Parse the initial leading panel size, either a percentage or a pixel value
// so that it is always a number that can be used for calculations
// The result is always at least minPanelSize
export const parseInitialLeadingPanelSize = (
  size: InitialLeadingPanelSize,
  containerSize: number | undefined,
  minPanelSize: PanelSize,
): PanelSize => {
  let result: number;

  if (typeof size === "number") {
    result = size;
  } else if (size.endsWith("%")) {
    if (containerSize === undefined) {
      // If container size is undefined, use the min panel size
      return minPanelSize;
    }
    result = (parseFloat(size.replace("%", "")) / 100) * containerSize;
  } else {
    result = parseFloat(size);
  }

  // Ensure the result is never smaller than minPanelSize and always an integer
  return Math.round(Math.max(result, minPanelSize));
};

const useResizablePanels = ({
  layout,
  initialLeadingPanelSize = "50%",
  minPanelSize,
  enabled = true,
  localStorageKey,
  containerRef: externalContainerRef,
}: {
  layout: "horizontal" | "vertical";
  initialLeadingPanelSize?: InitialLeadingPanelSize;
  minPanelSize: PanelSize;
  enabled?: boolean;
  localStorageKey: string;
  /**
   * Pass containerRef when the element already has a ref.
   * If it is not provided, the hook creates a ref.
   */
  containerRef?: RefObject<HTMLDivElement | null>;
}) => {
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = externalContainerRef ?? internalContainerRef;
  const leadingPanelRef = useRef<HTMLDivElement>(null);
  const trailingPanelRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const currentSizeRef = useRef<PanelSize>(minPanelSize);
  const [leadingPanelSize, setLeadingPanelSize] =
    useState<PanelSize>(minPanelSize);
  const [containerSize, setContainerSize] = useState<number>();

  const { userId } = useUserProfile();

  const leadingPanelSizeLocalStorageKey = useMemo(() => {
    return userStorageKey({
      userId,
      key: `${localStorageKey}-leading-panel-size-${layout}`,
    });
  }, [userId, localStorageKey, layout]);

  // Parse the initial leading panel size
  // Reset the initial size when the layout changes
  // useLayoutEffect avoids a one-frame flash when enabled becomes true before paint
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const initialContainerSize =
      layout === "vertical"
        ? containerRef.current?.getBoundingClientRect().height
        : containerRef.current?.getBoundingClientRect().width;

    setContainerSize(initialContainerSize);

    // Try to load from localStorage first
    const storedSize = localStorage.getItem(leadingPanelSizeLocalStorageKey);

    const size =
      storedSize !== null
        ? Math.round(parseFloat(storedSize))
        : parseInitialLeadingPanelSize(
            initialLeadingPanelSize,
            initialContainerSize,
            minPanelSize,
          );

    currentSizeRef.current = size;
    setLeadingPanelSize(size);
  }, [
    initialLeadingPanelSize,
    minPanelSize,
    layout,
    leadingPanelSizeLocalStorageKey,
    containerRef,
    enabled,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) {
        return;
      }

      const size =
        layout === "vertical"
          ? entry.contentRect.height
          : entry.contentRect.width;
      if (size === 0) {
        return;
      }

      setContainerSize(size);

      const clampedSize = Math.round(
        Math.max(
          minPanelSize,
          Math.min(size - minPanelSize, currentSizeRef.current),
        ),
      );
      if (clampedSize !== currentSizeRef.current) {
        currentSizeRef.current = clampedSize;
        setLeadingPanelSize(clampedSize);
      }
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [containerRef, enabled, layout, minPanelSize]);

  useEffect(() => {
    const container = containerRef.current;
    const leadingPanel = leadingPanelRef.current;
    const handle = handleRef.current;

    if (!handle) {
      return;
    }
    if (!container || !leadingPanel || !enabled) {
      handle.removeAttribute("tabindex");
      return;
    }

    const resizeTo = (size: number, availableSize: number) => {
      const constrainedSize = Math.round(
        Math.max(minPanelSize, Math.min(availableSize - minPanelSize, size)),
      );
      currentSizeRef.current = constrainedSize;
      setLeadingPanelSize(constrainedSize);
    };

    const startResizing = (event: MouseEvent) => {
      event.preventDefault(); // Prevents text selection
      isResizingRef.current = true;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResizing);
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizingRef.current || !container) {
        return;
      }

      const containerRect = container.getBoundingClientRect();

      const newSize =
        layout === "vertical"
          ? event.clientY - containerRect.top
          : event.clientX - containerRect.left;

      const availableSize =
        layout === "vertical" ? containerRect.height : containerRect.width;
      resizeTo(newSize, availableSize);
    };

    const stopResizing = () => {
      isResizingRef.current = false;
      // Save to localStorage when resizing stops
      localStorage.setItem(
        leadingPanelSizeLocalStorageKey,
        currentSizeRef.current.toString(),
      );
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const containerRect = container.getBoundingClientRect();
      const availableSize =
        layout === "vertical" ? containerRect.height : containerRect.width;
      let nextSize: number;

      switch (event.key) {
        case "ArrowLeft":
          if (layout !== "horizontal") {
            return;
          }
          nextSize = currentSizeRef.current - KEYBOARD_STEP;
          break;
        case "ArrowRight":
          if (layout !== "horizontal") {
            return;
          }
          nextSize = currentSizeRef.current + KEYBOARD_STEP;
          break;
        case "ArrowUp":
          if (layout !== "vertical") {
            return;
          }
          nextSize = currentSizeRef.current - KEYBOARD_STEP;
          break;
        case "ArrowDown":
          if (layout !== "vertical") {
            return;
          }
          nextSize = currentSizeRef.current + KEYBOARD_STEP;
          break;
        case "Home":
          nextSize = minPanelSize;
          break;
        case "End":
          nextSize = availableSize - minPanelSize;
          break;
        default:
          return;
      }

      event.preventDefault();
      resizeTo(nextSize, availableSize);
      localStorage.setItem(
        leadingPanelSizeLocalStorageKey,
        currentSizeRef.current.toString(),
      );
    };

    handle.tabIndex = 0;
    handle.addEventListener("mousedown", startResizing);
    handle.addEventListener("keydown", handleKeyDown);

    return () => {
      handle.removeAttribute("tabindex");
      handle.removeEventListener("mousedown", startResizing);
      handle.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
    };
  }, [
    minPanelSize,
    layout,
    leadingPanelSizeLocalStorageKey,
    containerRef,
    enabled,
  ]);

  useLayoutEffect(() => {
    if (leadingPanelRef.current) {
      const leadingPanel = leadingPanelRef.current;
      const trailingPanel = trailingPanelRef.current;
      const container = containerRef.current;
      const handle = handleRef.current;

      if (leadingPanel) {
        if (!enabled) {
          // When disabled, remove explicit sizing to let CSS take over
          leadingPanel.style.width = "";
          leadingPanel.style.height = "";
        } else {
          // When enabled, apply explicit sizing for resizable panels
          if (layout === "vertical") {
            leadingPanel.style.height = `${leadingPanelSize}px`;
            leadingPanel.style.width = "100%"; // Ensure full width in vertical layout
          } else {
            leadingPanel.style.width = `${leadingPanelSize}px`;
            leadingPanel.style.height = "100%"; // Ensure full height in horizontal layout
          }
        }
      }

      if (trailingPanel && !enabled) {
        // When disabled, remove explicit sizing to let CSS take over
        trailingPanel.style.width = "";
        trailingPanel.style.height = "";
      }

      // Also set trailing panel size, so that it is not limited by content height/width
      // This might occur when the trailing panel holds content that does not fit in the available space
      // E.g. when DetailsPanel is displayed
      if (trailingPanel && container && handle && enabled) {
        const containerRect = container.getBoundingClientRect();
        const handleRect = handle.getBoundingClientRect();
        const handleSize =
          layout === "vertical" ? handleRect.height : handleRect.width;
        const containerSize =
          layout === "vertical" ? containerRect.height : containerRect.width;

        const trailingPanelSize = Math.round(
          containerSize - leadingPanelSize - handleSize,
        );

        if (layout === "vertical") {
          trailingPanel.style.height = `${trailingPanelSize}px`;
          trailingPanel.style.width = "100%"; // Ensure full width in vertical layout
        } else {
          trailingPanel.style.width = `${trailingPanelSize}px`;
          trailingPanel.style.height = "100%"; // Ensure full height in horizontal layout
        }
      }
    }
  }, [leadingPanelSize, layout, enabled, containerRef]);

  return {
    containerRef,
    leadingPanelRef,
    trailingPanelRef,
    handleRef,
    leadingPanelSize,
    ariaValueNow: leadingPanelSize,
    ariaValueMin: minPanelSize,
    ariaValueMax:
      containerSize === undefined
        ? undefined
        : Math.max(minPanelSize, containerSize - minPanelSize),
  };
};

export default useResizablePanels;
